/**
 * @file DataPointsSubCategoryList.js
 * @description Displays and manages the list of Data Point Sub-Categories for a given Category, with add, delete, filter, and sorting functionality.
 */

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import styles from './DataPoints.module.scss';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { FilterModal, renderFilterIcon } from '../helpers/FilterComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import PaginationComponent from '../helpers/PaginationComponent';
import { ROUTES, ROUTE_BUILDERS } from '../../constants/routes';
import {
    getDataPointSubCategories,
    deleteDataPointSubCategory,
    updateDataPointSubCategory,
} from '../../services/individualService';
import { getText } from '../../i18n';

const SortOptions = {
    NAME: 'name',
    CODE: 'code',
};

const OrderOptions = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const ITEMS_PER_PAGE = 20;

const mapSubCategory = item => ({
    id: item.id,
    name: item.name || item.code,
    code: item.code,
    dataPointCategoryId: item.dataPointCategoryId,
    isDisabled: !(item.enabled ?? true),
});

const getStatusLabel = isDisabled =>
    isDisabled ? getText('msg_f4f4473df8cb') : getText('msg_df174a3f2faa');

const DataPointsSubCategoryList = () => {
    const { categoryId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);

    const categoryName =
        location.state?.categoryName ??
        getText('msg_e5cd3f9ae1a8', { categoryId });

    const [isLoading, setIsLoading] = useState(true);
    const [subCategoryList, setSubCategoryList] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        sort: SortOptions.NAME,
        order: OrderOptions.ASCENDING,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [filteredData, setFilteredData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isTogglingId, setIsTogglingId] = useState(null);

    const selectAllRef = useRef(null);

    useEffect(() => {
        loadSubCategories();
    }, [categoryId]);

    useEffect(() => {
        applyFiltersAndSort();
    }, [subCategoryList, selections, sortConfig, searchTerm]);

    useEffect(() => {
        if (selectAllRef.current) {
            const pageItems = getPaginatedItems();
            selectAllRef.current.indeterminate =
                selectedIds.length > 0 && selectedIds.length < pageItems.length;
        }
    }, [selectedIds, filteredData]);

    const loadSubCategories = async () => {
        setIsLoading(true);
        try {
            const data = await getDataPointSubCategories([Number(categoryId)]);
            setSubCategoryList(data.map(mapSubCategory));
        } catch (error) {
            Toast.error(getText('msg_7650a8d2cce1'));
        } finally {
            setIsLoading(false);
        }
    };

    const applyFiltersAndSort = () => {
        let result = [...subCategoryList];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(
                item =>
                    item.name.toLowerCase().includes(term) ||
                    item.code.toLowerCase().includes(term),
            );
        }

        Object.entries(selections).forEach(([column, selectedValues]) => {
            if (selectedValues && selectedValues.length > 0) {
                result = result.filter(item => {
                    if (column === 'isDisabled') {
                        const value = getStatusLabel(item[column]);
                        return selectedValues.includes(value);
                    }
                    return selectedValues.includes(item[column]?.toString());
                });
            }
        });

        result = result.sort((a, b) => {
            const aVal = a[sortConfig.sort]?.toString().toLowerCase() ?? '';
            const bVal = b[sortConfig.sort]?.toString().toLowerCase() ?? '';
            const comparison = aVal.localeCompare(bVal);
            return sortConfig.order === OrderOptions.ASCENDING
                ? comparison
                : -comparison;
        });

        setFilteredData(result);
        setTotalPages(Math.max(1, Math.ceil(result.length / ITEMS_PER_PAGE)));
        setCurrentPage(prev =>
            prev > Math.ceil(result.length / ITEMS_PER_PAGE) ? 1 : prev,
        );
    };

    const getPaginatedItems = () => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredData.slice(start, start + ITEMS_PER_PAGE);
    };

    const handleSort = sort => {
        setSortConfig({
            sort,
            order:
                sortConfig.sort === sort &&
                sortConfig.order === OrderOptions.ASCENDING
                    ? OrderOptions.DESCENDING
                    : OrderOptions.ASCENDING,
        });
    };

    const handleSearch = event => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
    };

    const clearFilters = () => {
        setSelections({});
        setSearchTerm('');
    };

    const openFilterModal = column => {
        setActiveModal(column);
    };

    const closeFilterModal = () => {
        setActiveModal(null);
    };

    const getOptions = column => {
        return Array.from(
            new Set(
                subCategoryList.map(item => {
                    if (column === 'isDisabled') {
                        return getStatusLabel(item[column]);
                    }
                    return item[column]?.toString();
                }),
            ),
        )
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
    };

    const handleSelectItem = id => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id],
        );
    };

    const handleSelectAllChange = event => {
        const pageItems = getPaginatedItems();
        setSelectedIds(
            event.target.checked ? pageItems.map(item => item.id) : [],
        );
    };

    const handleToggleDisabled = async id => {
        if (isTogglingId) return;
        setIsTogglingId(id);
        try {
            const subCategory = subCategoryList.find(item => item.id === id);
            const newEnabled = subCategory.isDisabled; // currently disabled → toggling to enabled
            await updateDataPointSubCategory({
                id,
                name: subCategory.name,
                code: subCategory.code,
                dataPointCategoryId: subCategory.dataPointCategoryId,
                enabled: newEnabled,
            });
            setSubCategoryList(prev =>
                prev.map(item =>
                    item.id === id
                        ? { ...item, isDisabled: !item.isDisabled }
                        : item,
                ),
            );
        } catch {
            Toast.error(getText('msg_ec769b6ad38d'));
        } finally {
            setIsTogglingId(null);
        }
    };

    const handleEditSubCategory = subCategory => {
        navigate(
            ROUTE_BUILDERS.dataPointsSubCategoryEdit(
                categoryId,
                subCategory.id,
            ),
        );
    };

    const handleAddSubCategory = () => {
        navigate(ROUTE_BUILDERS.dataPointsSubCategoryNew(categoryId));
    };

    const handleDeleteSelected = () => {
        if (!selectedIds.length) {
            Toast.error(getText('msg_3e0184ca3c84'));
            return;
        }

        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_643e65c612c1'),
            text:
                selectedIds.length > 1
                    ? getText('msg_946fc9d885d3')
                    : getText('msg_e1e1db57ba99'),
            onYes: async () => {
                const errors = [];
                for (const id of selectedIds) {
                    try {
                        await deleteDataPointSubCategory(id);
                    } catch {
                        errors.push(id);
                    }
                }
                if (errors.length === 0) {
                    Toast.success(
                        getText('msg_8676eb767131', {
                            count: selectedIds.length,
                        }),
                    );
                } else {
                    Toast.error(
                        getText('msg_2cb394429999', {
                            count: errors.length,
                        }),
                    );
                }
                setSelectedIds([]);
                await loadSubCategories();
            },
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
    };

    const renderSortArrow = sort => {
        if (sortConfig.sort !== sort) return null;
        return (
            <span className="mx-2">
                <svg width="11" height="6" viewBox="0 0 11 6" fill="none">
                    <path
                        d={
                            sortConfig.order === OrderOptions.ASCENDING
                                ? 'M1 5.25L5.25 0.75L9.5 5.25'
                                : 'M1 0.75L5.25 5.25L9.5 0.75'
                        }
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    };

    const pageItems = getPaginatedItems();
    const allPageSelected =
        pageItems.length > 0 &&
        pageItems.every(item => selectedIds.includes(item.id));

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center py-3 px-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <span
                                    className={styles.categoryNameLink}
                                    onClick={() => navigate(ROUTES.DATA_POINTS)}
                                >
                                    {getText('msg_1877c58443d0')}
                                </span>
                                <i
                                    className="bi bi-chevron-right text-muted"
                                    style={{ fontSize: '0.75rem' }}
                                />
                                <span className="text-muted">
                                    {categoryName}
                                </span>
                            </div>
                            <p className="ing-portals-subtitle mt-1 ms-0">
                                {getText('msg_7db9e96774d7', {
                                    categoryName,
                                })}
                            </p>
                        </div>
                        <div className="float-end d-flex align-items-center align-self-baseline">
                            <span
                                type="button"
                                onClick={handleDeleteSelected}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-trash3 me-2" />
                                {getText('msg_f6fdbe48dc54')}
                            </span>
                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>
                            <span
                                type="button"
                                onClick={handleAddSubCategory}
                                className="ms-3 me-4 button-base ing-button-primary-left align-middle"
                            >
                                <i className="bi bi-plus-lg me-1" />
                                {getText('msg_61cc55aa0453')}
                            </span>
                        </div>
                    </div>

                    <div className="px-4 mb-3">
                        <div className="input-group" style={{ maxWidth: 320 }}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={getText('msg_69807f2cf6d5')}
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                            <span className="input-group-text">
                                <i className="bi bi-search" />
                            </span>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="text-center mt-5">
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        </div>
                    ) : subCategoryList.length === 0 ? (
                        <NotFoundBox
                            backgroundColor="#f0f4ff"
                            title={getText('msg_c7c03a60d02c')}
                            description={getText('msg_8c91d9502472')}
                        />
                    ) : (
                        <div className="table-responsive-xxl w-100">
                            <table className="table table-hover align-middle">
                                <thead className="custom-table-header table-light">
                                    <tr>
                                        <th className="text-center checkbox-column-head">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                ref={selectAllRef}
                                                checked={allPageSelected}
                                                onChange={handleSelectAllChange}
                                            />
                                        </th>
                                        <th
                                            style={{ cursor: 'pointer' }}
                                            className="position-relative"
                                            onClick={() =>
                                                handleSort(SortOptions.NAME)
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_5cf1680d2b86')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.NAME,
                                                )}
                                            </span>
                                            <span
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    openFilterModal('name');
                                                }}
                                                className={classNames(
                                                    styles.cursorPointer,
                                                    'pe-3',
                                                )}
                                            >
                                                {renderFilterIcon(
                                                    selections['name']?.length >
                                                        0,
                                                )}
                                            </span>
                                            {activeModal === 'name' && (
                                                <FilterModal
                                                    column={activeModal}
                                                    options={getOptions(
                                                        activeModal,
                                                    )}
                                                    selectedOptions={
                                                        selections[
                                                            activeModal
                                                        ] || []
                                                    }
                                                    onApply={handleApply}
                                                    onClose={closeFilterModal}
                                                />
                                            )}
                                        </th>
                                        <th
                                            style={{ cursor: 'pointer' }}
                                            className="position-relative"
                                            onClick={() =>
                                                handleSort(SortOptions.CODE)
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_adac69379a62')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.CODE,
                                                )}
                                            </span>
                                            <span
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    openFilterModal('code');
                                                }}
                                                className={classNames(
                                                    styles.cursorPointer,
                                                    'pe-3',
                                                )}
                                            >
                                                {renderFilterIcon(
                                                    selections['code']?.length >
                                                        0,
                                                )}
                                            </span>
                                            {activeModal === 'code' && (
                                                <FilterModal
                                                    column={activeModal}
                                                    options={getOptions(
                                                        activeModal,
                                                    )}
                                                    selectedOptions={
                                                        selections[
                                                            activeModal
                                                        ] || []
                                                    }
                                                    onApply={handleApply}
                                                    onClose={closeFilterModal}
                                                />
                                            )}
                                        </th>
                                        <th>
                                            <span className="table-column-header-text">
                                                {getText('msg_20063ad90532')}
                                            </span>
                                        </th>
                                        <th>
                                            <span className="table-column-header-text">
                                                {getText('msg_5301648dcf6b')}
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageItems.map(subCategory => (
                                        <tr key={subCategory.id}>
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selectedIds.includes(
                                                        subCategory.id,
                                                    )}
                                                    onChange={() =>
                                                        handleSelectItem(
                                                            subCategory.id,
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td>
                                                <span>{subCategory.name}</span>
                                            </td>
                                            <td>{subCategory.code}</td>
                                            <td>
                                                <div
                                                    className="form-switch form-switch-locked"
                                                    style={{
                                                        textAlign: 'left',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <input
                                                        className="form-check-input custom-switch-input"
                                                        type="checkbox"
                                                        checked={
                                                            !subCategory.isDisabled
                                                        }
                                                        onChange={e => {
                                                            e.stopPropagation();
                                                            handleToggleDisabled(
                                                                subCategory.id,
                                                            );
                                                        }}
                                                        onClick={e =>
                                                            e.stopPropagation()
                                                        }
                                                        disabled={
                                                            isTogglingId ===
                                                            subCategory.id
                                                        }
                                                    />
                                                </div>
                                            </td>
                                            <td>
                                                <span
                                                    className={
                                                        styles.viewButton
                                                    }
                                                    onClick={() =>
                                                        handleEditSubCategory(
                                                            subCategory,
                                                        )
                                                    }
                                                >
                                                    <i className="bi bi-pencil" />
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {filteredData.length > 0 && (
                        <PaginationComponent
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DataPointsSubCategoryList;
